// src/features/fourlink-config/components/blocks/Block2Steps/MatrizTareasAsignacion.jsx

import React, { useMemo } from 'react';
import { TurnoFilter } from '../../shared/TurnoFilter';

export const MatrizTareasAsignacion = ({
  selectedTipologias,
  selectedEquipos,
  selectedTareas,
  equiposConfig,
  tipologiaEquipoMatrix,
  tareasAsignacionMatrix,
  tareasPorCategoriaDisponibles,
  onToggleTareaAsignacion,
  // Filtro de turnos
  turnoFilterOptions,
  turnoFilterSelected,
  turnoFilterOpen,
  turnoFilterSearch,
  onTurnoFilterToggle,
  onTurnoFilterSearchChange,
  onTurnoFilterSelectionToggle,
  onTurnoFilterClear,
  turnoFilterRef,
  turnosAsignadosPorTarea,
}) => {
  const isTurnoFilterActive = turnoFilterSelected && turnoFilterSelected.length > 0;
  
  const tareaCumpleFiltroDeTurnos = (tareaId) => {
    if (!isTurnoFilterActive) return true;
    
    const metadata = turnosAsignadosPorTarea[tareaId];
    if (!metadata || !metadata.habilitado || !Array.isArray(metadata.ids) || metadata.ids.length === 0) {
      return false;
    }
    return metadata.ids.some((turnoId) => turnoFilterSelected.includes(turnoId));
  };
  
  const turnoFilterButtonText = useMemo(() => {
    if (!turnoFilterSelected || turnoFilterSelected.length === 0) {
      return 'Filtrar por turno';
    }
    
    const labels = turnoFilterSelected
      .map(id => turnoFilterOptions.find(opt => opt.id === id)?.label)
      .filter(Boolean);
    
    if (labels.length === 0) {
      return 'Filtrar por turno';
    }
    
    if (labels.length <= 2) {
      return labels.join(', ');
    }
    
    const [first, second] = labels;
    const remaining = labels.length - 2;
    return `${first}, ${second} +${remaining}`;
  }, [turnoFilterSelected, turnoFilterOptions]);
  
  const hayCoincidenciasFiltroTurno = useMemo(() => {
    if (!isTurnoFilterActive) return true;
    
    return Object.values(tareasPorCategoriaDisponibles).some((lista) =>
      lista.some((tarea) => selectedTareas.includes(tarea.id) && tareaCumpleFiltroDeTurnos(tarea.id))
    );
  }, [tareasPorCategoriaDisponibles, selectedTareas, isTurnoFilterActive, turnosAsignadosPorTarea, turnoFilterSelected]);
  
  // Crear mapa de tareas por ID
  const tareasPorId = useMemo(() => {
    const map = new Map();
    Object.entries(tareasPorCategoriaDisponibles || {}).forEach(([categoria, listaTareas]) => {
      listaTareas.forEach((tarea) => {
        if (tarea && tarea.id !== undefined && tarea.id !== null) {
          const key = String(tarea.id);
          if (!map.has(key)) {
            map.set(key, { tarea, categoria });
          }
        }
      });
    });
    return map;
  }, [tareasPorCategoriaDisponibles]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Matriz de Tareas y Asignación</h2>
          <p className="text-gray-600">Asigna las tareas seleccionadas a cada tipología de centro:</p>
        </div>
        
        {/* Filtro de turnos */}
        {turnoFilterOptions.length > 0 && (
        <TurnoFilter
            isOpen={turnoFilterOpen}
            search={turnoFilterSearch}
            selected={turnoFilterSelected}
            filteredOptions={turnoFilterOptions}
            buttonText={turnoFilterButtonText}
            isActive={isTurnoFilterActive}
            dropdownRef={turnoFilterRef}
            onToggle={onTurnoFilterToggle}
            onSearchChange={onTurnoFilterSearchChange}
            onToggleSelection={onTurnoFilterSelectionToggle}
            onClear={onTurnoFilterClear}
        />
        )}
      </div>
      
      {isTurnoFilterActive && !hayCoincidenciasFiltroTurno && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          No hay tareas que coincidan con los turnos seleccionados.
        </div>
      )}

      <div className="space-y-8">
        {selectedTipologias.map((tipologia, tipologiaIndex) => {
          const tipologiaCombinations = [];
          selectedEquipos.forEach(equipo => {
            const selectedQRs = tipologiaEquipoMatrix[`${tipologia}-${equipo}`] || [];
            const equipoQRs = equiposConfig[equipo]?.qrs || [];
            
            selectedQRs.forEach(qrId => {
              const qr = equipoQRs.find(q => q.id === qrId && q.active);
              if (qr) {
                tipologiaCombinations.push({
                  equipo,
                  qrId,
                  qrName: qr.name
                });
              }
            });
          });

          if (tipologiaCombinations.length === 0) {
            return (
              <div key={tipologia} className="border rounded-lg p-6 bg-gray-50">
                <h3 className="text-xl font-bold text-blue-800 mb-4">{tipologia}</h3>
                <div className="text-center py-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-700">No hay equipos/QRs asignados para esta tipología</p>
                  <p className="text-yellow-600 text-sm mt-1">Configura la matriz anterior</p>
                </div>
              </div>
            );
          }

          const isFirstTipologia = tipologiaIndex === 0;

          return (
            <div key={tipologia} className="border rounded-lg p-6 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-blue-800">{tipologia}</h3>
                  {isFirstTipologia && (
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                      TIPOLOGÍA MAESTRA
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {tipologiaCombinations.length} combinación{tipologiaCombinations.length !== 1 ? 'es' : ''}
                </span>
              </div>

              {isFirstTipologia && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>Tipología Maestra:</strong> Las selecciones aquí se replicarán automáticamente 
                    en las demás tipologías donde existan las mismas combinaciones equipo-QR.
                  </p>
                </div>
              )}
              
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto relative">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr>
                      <th className="sticky top-0 z-20 bg-white border border-gray-300 px-3 py-2 text-left font-semibold min-w-[200px] shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                        Tareas
                      </th>
                      {tipologiaCombinations.map((combo, index) => (
                        <th
                          key={index}
                          className="sticky top-0 z-20 bg-white border border-gray-300 px-2 py-2 text-center font-semibold min-w-[120px] shadow-[0_1px_0_rgba(0,0,0,0.06)]"
                        >
                          <div className="space-y-1 text-xs">
                            <div className="text-gray-600">{combo.equipo}</div>
                            <div className="text-gray-800 font-medium">{combo.qrName}</div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {Object.entries(tareasPorCategoriaDisponibles).map(([categoria, tareas]) => {
                      const tareasSeleccionadas = tareas
                        .filter((tarea) => selectedTareas.includes(tarea.id))
                        .filter((tarea) => tareaCumpleFiltroDeTurnos(tarea.id));
                      if (tareasSeleccionadas.length === 0) return null;

                      return (
                        <React.Fragment key={categoria}>
                          <tr className="bg-blue-50">
                            <td className="border border-gray-300 px-3 py-2 font-bold text-blue-800">
                              {categoria}
                            </td>
                            {tipologiaCombinations.map((_, i) => (
                              <td key={i} className="border border-gray-300 px-2 py-2 bg-blue-50"></td>
                            ))}
                          </tr>

                          {tareasSeleccionadas.map(tarea => (
                            <tr key={tarea.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-3 py-2">
                                <div className="font-medium text-gray-900 text-sm">{tarea.name}</div>
                              </td>

                              {tipologiaCombinations.map((combo, i) => {
                                const isChecked = tareasAsignacionMatrix[`${tarea.id}-${tipologia}-${combo.equipo}-${combo.qrId}`] || false;
                                const isFirstTipologia = selectedTipologias.indexOf(tipologia) === 0;

                                let isAutoActivated = false;
                                if (!isFirstTipologia && isChecked) {
                                  const firstTipologia = selectedTipologias[0];
                                  const k = `${tarea.id}-${firstTipologia}-${combo.equipo}-${combo.qrId}`;
                                  isAutoActivated = tareasAsignacionMatrix[k] || false;
                                }

                                return (
                                  <td key={i} className="border border-gray-300 px-2 py-2 text-center">
                                    {isAutoActivated ? (
                                      <div
                                        onClick={() => onToggleTareaAsignacion(tarea.id, tipologia, combo.equipo, combo.qrId)}
                                        className="w-4 h-4 border-2 border-gray-400 rounded bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors mx-auto"
                                        title="Activado automáticamente desde la primera tipología"
                                      >
                                        {isChecked && (
                                          <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                      </div>
                                    ) : (
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => onToggleTareaAsignacion(tarea.id, tipologia, combo.equipo, combo.qrId)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      
      {selectedTareas.length === 0 && (
        <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-500">No hay tareas seleccionadas para asignar</p>
        </div>
      )}
    </div>
  );
};
